/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import { promisify } from 'util';
import React, { useEffect, useState, useRef } from 'react';
import { css, StyleSheet } from 'aphrodite';
import { connect, useDispatch } from 'react-redux';
import { change, getFormValues, submit } from 'redux-form';
import { ActionButton, colors, fonts, LoadingSpinner, mixins } from '@blend/consumer-components';
import { AxiosError } from 'axios';
import { mediaBreakpoints } from '../../../common/responsive';

import { State } from '../../../redux';
import { useRootSelector } from '../../../hooks/useRootSelector';
import { BaseInputConfig } from '../TaskInputs/types';
import { TaskInputs } from '../TaskInputs/TaskInputs';
import { participantAPI } from '../../../api/participant';
import { useWebsocketEventSubscriber } from '../../../hooks/useWebsocketEventSubscriber';
import { WEBSOCKET_CHANNELS } from '../../../shared/constants/websocketChannels';
import { initializeWebsocketEventSubscriber } from '../../../api/websocket/eventSubscriber';
import { WebsocketProviders } from '../../../api/websocket/types';
import { useRoom } from '../../../hooks/useRoom';
import { Socure } from './socureTypes';
import { getConfig } from '@utils/configs';
import { diagnostics } from '@utils/diagnostics';
import { get, pick } from '@utils/lodash';

const SESSION_STORAGE_SOCURE_INIT_KEY = 'SOCURE_INIT';

declare global {
  interface Window {
    SocureInitializer: Socure.SocureInit;
    Socure: Socure.SocureLib;
    socureSdkInitialized: boolean;
  }
}

const FORM_NAME = 'task';

const SOCURE_PROCESS_ENUM = {
  UPLOAD_ONLY: 1,
  DOC_VERIFY: 2,
  DOC_VERIFY_AND_PHONE_RISK: 3,
};

const styles = StyleSheet.create({
  resendWrapper: {
    display: 'flex',
    justifyContent: 'space-between',
    ...mixins.marginVertical(20),
    [mediaBreakpoints.mobile]: {
      display: 'block',
    },
  },
  inputWrapper: {
    width: '100%',
    marginRight: 8,
  },
  buttonWrapper: {
    marginTop: 56,
    [mediaBreakpoints.mobile]: {
      marginTop: 0,
    },
  },
  button: {
    height: 50,
    fontSize: 14,
    fontWeight: fonts.weight.medium,
  },
  resendText: {
    fontSize: 14,
    fontWeight: fonts.weight.medium,
  },
  loadingContainer: {
    padding: '150px 0',
  },
  socureWrapper: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  hideSocureContainer: {
    display: 'none',
  },
});

export namespace SocureIDV {
  export interface DispatchProps {}
  export interface OwnProps {
    opts: {
      task: {
        template: {
          customProps: {
            mobileNumber: string;
            idvClientKey: string;
            showBackToUserSelectButton?: boolean;
          };
        };
      };
    };
  }
  export interface StateProps {
    currentMobileNumber: string;
  }

  export type Props = StateProps & DispatchProps & OwnProps;
}

const fixedSocureReset = async () => {
  // cleanup sessionStorage for all Socure specific keys
  ['key', 'documentVerificationToken', 'publicApiKey'].forEach(socureKey => {
    window.sessionStorage.removeItem(socureKey);
  });

  // check if we have initialized SDK previously, persistent between page reloads
  if (window.sessionStorage.getItem(SESSION_STORAGE_SOCURE_INIT_KEY)) {
    if (window.Socure) {
      try {
        // try to reset Socure context if active
        await window.Socure.reset();
      } catch (e) {
        // eslint-disable-next-line no-console
        console.debug('window.Socure.reset() failed', e);
      }
    }

    // mark session as cleaned
    window.sessionStorage.removeItem(SESSION_STORAGE_SOCURE_INIT_KEY);
    // reload the page to wipe any in-memory data that persisted from last attempt
    window.location.reload();
    // wait forever to prevent anything IDV from starting in this context
    await promisify(setTimeout)(1000000000000);
  }
};

export const _SocureIDV: React.FunctionComponent<SocureIDV.Props> = ({ opts, currentMobileNumber }) => {
  const socureSuccessRef = useRef(false);
  const [isSocureLoading, setIsSocureLoading] = useState(true);
  const [currentSessionId, setCurrentSessionId] = useState<string>('');
  const { roomId } = useRoom();
  const dispatch = useDispatch();
  const publicKey = opts.task.template.customProps.idvClientKey;
  const socureMountId = 'websdk';

  const onSuccess = (response: Socure.SuccessResponse, isWebhookResponse?: boolean) => {
    // since we call onSuccess from webhook and from the websdk, we need to make sure to only call it once
    if (socureSuccessRef.current) {
      void participantAPI.rooms.sendBrowserLog({
        message: '[Socure] onSuccess already called',
        data: { ...pick(response, 'eventId', 'status', 'referenceId', 'documentUuid'), isWebhookResponse },
      });
      return;
    }
    socureSuccessRef.current = true;
    try {
      void participantAPI.rooms.sendBrowserLog({
        message: '[Socure] onSuccess',
        data: {
          ...pick(
            response,
            'eventId',
            'status',
            'verificationLevel',
            'key',
            'referenceId',
            'documentUuid',
            'verifyResult.documentVerification.decision',
            'verifyResult.referenceId',
          ),
          isWebhookResponse,
        },
      });

      dispatch(change(FORM_NAME, 'referenceId', response.verifyResult.referenceId));
      dispatch(change(FORM_NAME, 'documentId', response.documentUuid));
      dispatch(submit(FORM_NAME));
    } catch (err) {
      diagnostics.error(err as AxiosError, {
        additionalMeta: `Socure success response has changed shape: ${JSON.stringify(response.verifyResult)}`,
      });
    }
  };

  useEffect(() => {
    async function setMobileNumber() {
      // eslint-disable-next-line @typescript-eslint/await-thenable
      await dispatch(change(FORM_NAME, 'mobileNumber', currentMobileNumber));
    }
    setMobileNumber();
  }, []);

  const handleIdvServiceError = (error: Socure.ErrorResponse | unknown) => {
    const errorData = pick(error, 'eventId', 'status', 'verificationLevel', 'key', 'referenceId');
    // istanbul ignore next
    const message = '[Socure] onError:' + (error instanceof Error ? error.message : '');
    void participantAPI.rooms.sendBrowserLog({
      message,
      level: 'error',
      data: errorData,
    });

    // eslint-disable-next-line no-console
    console.error('Socure error', error);
    diagnostics.error(new Error(message), { err: errorData });
    return;
  };

  const onError = (error: Socure.ErrorResponse) => {
    handleIdvServiceError(error);
  };

  const onProgress = (progress: Socure.ProgressResponse) => {
    void participantAPI.rooms.sendBrowserLog({
      message: '[Socure] onProgress',
      data: pick(progress, 'eventId', 'status', 'verificationLevel', 'key', 'referenceId'),
    });

    return;
  };

  const initializeSocure = async () => {
    const config = {
      onProgress,
      onSuccess,
      onError,
      qrCodeNeeded: true,
    };
    try {
      const socureLib = await window.SocureInitializer.init(publicKey);
      const session = await participantAPI.sessions.fetchSession();

      await socureLib.init(publicKey, `#${socureMountId}`, config);
      if (!currentMobileNumber.startsWith('+1')) {
        currentMobileNumber = `+1${currentMobileNumber}`;
      }
      const result = await socureLib.start(SOCURE_PROCESS_ENUM.DOC_VERIFY, {
        mobileNumber: currentMobileNumber,
        customerUserId: session.id,
      });
      setCurrentSessionId(session.id);

      setIsSocureLoading(false);
      window.sessionStorage.setItem(SESSION_STORAGE_SOCURE_INIT_KEY, 'true');
      void participantAPI.rooms.sendBrowserLog({
        message: '[Socure] started',
        data: pick(result, 'accountId', 'eventId', 'referenceId', 'status', 'uid', 'verificationLevel'),
      });
    } catch (err) {
      // the error socure emits is just an object
      handleIdvServiceError(err as Socure.ErrorResponse);
    }
  };

  const getNewMobileNumber = (): BaseInputConfig => {
    // configuration required by TaskInputs
    return { label: "Didn't receive a link?", id: 'mobileNumber', type: 'phone', required: false };
  };

  const resendSMS = () => {
    // socure does not expose a resend sms method. This is what they suggest; we are
    // using cleanup instead of reset because reset unmounts the socure web sdk app, and
    // re-initializing does not remount it for some reason, so we don't poll for updates
    // while the user is uploading their docs via the socure capture app.

    // continued... However, testing showed that using clenaup() was causing status checks to end
    // relying on the Socure.reset() within initializeSocure function appears to be sufficient
    initializeSocure();
  };

  const websocketEventHandler = (event: Socure.SuccessResponse) => {
    onSuccess(event, true);
  };
  useWebsocketEventSubscriber(WEBSOCKET_CHANNELS.idvSuccess(currentSessionId), websocketEventHandler, {
    enabled: getConfig().isWebsocketEnabled && !!currentSessionId,
  });

  useEffect(() => {
    async function newWindowSocureInitialize() {
      await fixedSocureReset();
      initializeSocure();
    }

    initializeWebsocketEventSubscriber(WebsocketProviders.FAYE, roomId, true);

    newWindowSocureInitialize();
  }, []);

  const { isMobileView } = useRootSelector(state => state.layout);

  return (
    <div>
      <div className={css(styles.socureWrapper)}>
        {isSocureLoading ? (
          <span title="loading..." className={css(styles.loadingContainer)}>
            <LoadingSpinner size={'lg'} color={colors.catalyst} />
          </span>
        ) : null}
        {/* this div is what socure attaches its UI components to. They are responsible
        for polling socure for updates https://developer.socure.com/guide/websdk */}
        <div id={socureMountId} className={css(isSocureLoading && styles.hideSocureContainer)} />
      </div>

      <div className={css(styles.resendWrapper)}>
        <div className={css(styles.inputWrapper)}>
          <TaskInputs inputs={[getNewMobileNumber()]} />
        </div>
        <div className={css(styles.buttonWrapper)}>
          <ActionButton
            data-dd-privacy="allow"
            color={colors.catalyst}
            data-it="Socure-resendSMS"
            type="button"
            option="secondary"
            sizing={isMobileView ? 'full' : 'md'}
            onClick={resendSMS}
            customStyle={styles.button}
          >
            Resend SMS
          </ActionButton>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state: State, props: SocureIDV.OwnProps) => {
  const modifyIDV = () => {
    const initMobileNumber = props.opts.task.template.customProps.mobileNumber;
    const formValues = getFormValues(FORM_NAME)(state);
    /* istanbul ignore next */
    return !formValues ? initMobileNumber : get(formValues, 'mobileNumber');
  };

  return {
    currentMobileNumber: modifyIDV(),
  };
};

export const SocureIDV = connect<SocureIDV.StateProps, {}, SocureIDV.OwnProps, State>(mapStateToProps)(_SocureIDV);
